import React, { useState, useEffect } from "react";
import {
  signUpUser,
  securecontactverification,
  verifyOtp,
  initiateMfa,
  verifyMfa,
  saveMfaVerificationData,
  authUserByUserGuid,
  signInUser,
  initiateVerification,
} from "./actions";
import { getPortalCD, loadScript, readCookie, removeScript } from "../../utils/helpers";
import { getTenantGroupId, getTenantId, getTokenExIframeJs } from "../../utils/general-config";
import { createHmac, getTokenExID, getTokenExScheme, getUTCTimeStamp } from "../../utils/payment-config";

const SignupWithOtp = () => {
  const [step, setStep] = useState("signup");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [mfaOptIn, setMfaOptIn] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpTarget, setOtpTarget] = useState("");
  const [userGuid, setUserGuid] = useState("");
  const [signupComplete, setSignupComplete] = useState(false);
  const [postMfaSignInData, setPostMfaSignInData] = useState(null);
  const [tokenizePhone, setTokenizePhone] = useState("");

  const isSignup = step === "signup";
  const isSignin = step === "signin";
  const origin = window.location.origin;

  useEffect(() => {
    loadScript(getTokenExIframeJs());
    return () => {
      removeScript(getTokenExIframeJs());
    };
  }, []);

  const handleInitiateContactVerification = async (contactType) => {
    setLoading(true);
    try {
      const payload = {
        UserGuid: userGuid || readCookie("userguid"),
        ContactType: contactType === "email" ? 1 : 2,
        TenantGroupId: getTenantGroupId(),
      };
      await initiateVerification(payload);
      setOtpTarget(contactType);
      setMessage(`OTP sent to your ${contactType}`);
    } catch (err) {
      setMessage("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const trimmedOtp = otp.trim();
    if (!trimmedOtp) return setMessage("Enter a valid OTP");

    // For phone or MFA, check 6-digit format
    if ((otpTarget === "phone" || otpTarget === "mfa") && trimmedOtp.length !== 6) {
      return setMessage("Enter a valid 6-digit OTP");
    }
    setLoading(true);
    try {
      if (otpTarget === "email") {
        await verifyOtp(userGuid || readCookie("userguid"), trimmedOtp);
        setEmailVerified(true);
      } else if (otpTarget === "phone") {
        await verifyOtp(userGuid || readCookie("userguid"), trimmedOtp);
        setPhoneVerified(true);
      } else if (otpTarget === "mfa") {
        await verifyMfa(userGuid || readCookie("userguid"), trimmedOtp);
        setMessage("MFA Verified. Signing you in...");
        await handleFinalSignIn();
        setStep("welcome");
      }
      setOtp("");
      setOtpTarget("");
      if (otpTarget !== "mfa") setMessage("Verified successfully");
    } catch (err) {
      setMessage("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const tokenizePhoneReactFunction = () => {
    window.reactFunction = () => {
      const token = localStorage.getItem("token");
      setTokenizePhone(token);
      localStorage.removeItem("token");
      handleSignup(token);
    };
  };

  const handleSignup = async (tokenizedPhone) => {
    if (!username || !password || !tokenizedPhone) return setMessage("Please fill all fields");

    const params = {
      email: username.trim(),
      password: password.trim(),
      portalCD: getPortalCD(),
      tenantId: getTenantId(),
      countryCD: readCookie("country"),
      currencyCD: readCookie("currency"),
      tenantGroupId: getTenantGroupId(),
      twoFactorEnabled: mfaOptIn,
      countryCode,
      phoneNumber: tokenizedPhone,
    };

    setLoading(true);
    try {
      const response = await signUpUser(params);
      if (response?.isValid) {
        const guid = response.userGuid;
        setUserGuid(guid);
        document.cookie = `userguid=${guid}`;
        setSignupComplete(true);
        setMessage("Signup successful.");
        if (mfaOptIn) {
          const mfaData = {
            userGuid: guid,
            ipAddress: "192.168.1.1",
            loginDeviceType: "Desktop",
            userAgent: "Mozilla/5.0",
            userAgentHash: "d41d8cd98f00b204e9800998ecf8427e",
            macAddress: "00:1A:2B:3C:4D:5E",
            mfaRequired: true,
            mfaVerified: false,
            visitId: readCookie("visitGuid"),
            tenantId: getTenantId(),
          };
          await saveMfaVerificationData(mfaData);
          await initiateMfa(guid);
          setOtpTarget("mfa");
          setMessage("MFA OTP sent. Please verify.");
        }
      } else {
        setMessage("Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSignIn = async () => {
    const payload = {
      username: username.trim(),
      password: password.trim(),
      tenantGroupId: getTenantGroupId(),
    };
    try {
      await signInUser(payload);
    } catch {
      setMessage("Sign-in after MFA failed");
    }
  };

  const handleSignin = async () => {
    if (!username || !password) return setMessage("Please enter username and password");
    setLoading(true);
    try {
      const response = await signInUser({
        username: username.trim(),
        password: password.trim(),
        tenantGroupId: getTenantGroupId(),
      });

      if (response?.validUserGuid) {
        setUserGuid(response.validUserGuid);
        document.cookie = `userguid=${response.validUserGuid}`;
        if (mfaOptIn) {
          const mfaData = {
            userGuid: response.validUserGuid,
            ipAddress: "192.168.1.1",
            loginDeviceType: "Desktop",
            userAgent: "Mozilla/5.0",
            userAgentHash: "d41d8cd98f00b204e9800998ecf8427e",
            macAddress: "00:1A:2B:3C:4D:5E",
            mfaRequired: true,
            mfaVerified: false,
            visitId: readCookie("visitGuid"),
            tenantId: getTenantId(),
          };
          await saveMfaVerificationData(mfaData);
          await initiateMfa(response.validUserGuid);
          setOtpTarget("mfa");
          setMessage("MFA OTP sent. Please verify.");
        } else {
          setStep("welcome");
        }
      } else {
        setMessage("Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderVerifyLinks = (contactType) => {
    const isVerified = contactType === "email" ? emailVerified : phoneVerified;
    const showVerify = (step === "signin" || step === "welcome") && userGuid;
    return (
      showVerify && (
        <span className="ml-2 text-sm">
          {isVerified ? (
            <span className="text-green-600">Verified</span>
          ) : (
            <button className="text-blue-600 underline" onClick={() => handleInitiateContactVerification(contactType)}>
              Verify
            </button>
          )}
        </span>
      )
    );
  };

  return (
    <div className="max-w-sm mx-auto mt-10 p-6 bg-white shadow-md rounded-2xl text-center">
      <input type="hidden" id="txOrigin" value={origin} />
      <input type="hidden" id="txID" value={getTokenExID()} />
      <input type="hidden" id="txTokenSchemeMFA" value={getTokenExScheme()} />
      <input type="hidden" id="tokenExToken" value="" />
      <input type="hidden" id="hmacMFA" value={createHmac()} />
      <input type="hidden" id="timeStamp" value={getUTCTimeStamp()} />
      {step !== "welcome" && <h2 className="text-2xl font-semibold mb-4">{isSignup ? "Sign Up" : "Sign In"}</h2>}
      {message && <p className="mb-3 text-sm text-gray-600">{message}</p>}
      {loading && <div className="loader mx-auto mb-4" />}

      {!loading && step !== "welcome" && (
        <>
          <div className="mb-3 flex items-center">
            <input
              type="email"
              placeholder="Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="flex-1 p-2 border rounded"
              disabled={signupComplete}
            />
            {renderVerifyLinks("email")}
          </div>

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-3 p-2 border rounded"
            disabled={signupComplete}
          />

          {isSignup && (
            <div className="mb-3 flex items-center gap-2">
              <input
                type="text"
                placeholder="+1"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-1/3 p-2 border rounded"
                disabled={signupComplete}
              />
              <div id="tokenExIframePhoneDiv" style={{ height: "30px" }}></div>
              {renderVerifyLinks("phone")}
            </div>
          )}

          {!signupComplete && (
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="mfaOptIn"
                checked={mfaOptIn}
                onChange={(e) => setMfaOptIn(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="mfaOptIn" className="text-sm text-gray-700">
                Enable Multi-Factor Authentication
              </label>
            </div>
          )}

          {!signupComplete && isSignup && (
            <button
              id="btnSignUp"
              onClick={tokenizePhoneReactFunction}
              className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
            >
              Sign Up
            </button>
          )}

          {isSignin && (
            <button onClick={handleSignin} className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700">
              Sign In
            </button>
          )}

          <p className="mt-3 text-sm">
            {isSignup ? "Already have an account?" : "Don't have an account?"}{" "}
            <button onClick={() => setStep(isSignup ? "signin" : "signup")} className="text-blue-600 underline">
              {isSignup ? "Sign In" : "Sign Up"}
            </button>
          </p>
        </>
      )}

      {!loading && otpTarget && (
        <>
          <input
            type="text"
            placeholder={otpTarget === "email" ? "Enter email token (GUID)" : "Enter 6-digit OTP"}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={otpTarget === "email" ? 36 : 6}
            className="w-full mt-4 mb-3 p-2 border rounded text-center tracking-widest"
          />

          <button onClick={handleVerifyOtp} className="w-full bg-green-600 text-white p-2 rounded hover:bg-green-700">
            Verify OTP
          </button>
        </>
      )}

      {step === "welcome" && (
        <div className="animate-fadeIn text-left">
          <h2 className="text-2xl font-bold mb-2 text-green-700 text-center">🎉 Welcome to</h2>
          <h1 className="text-3xl font-extrabold text-blue-800 mb-4 text-center">SecureSimpli Beta Mode</h1>

          <div className="mb-3">
            <label className="text-sm font-medium text-gray-700">Email:</label>
            <div className="flex items-center">
              <span>{username}</span>
              {renderVerifyLinks("email")}
            </div>
          </div>

          <div className="mb-3">
            <label className="text-sm font-medium text-gray-700">Phone:</label>
            <div className="flex items-center">
              <span>
                {countryCode} {phone || tokenizePhone}
              </span>
              {renderVerifyLinks("phone")}
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center">Thank you for signing {isSignup ? "up" : "in"} securely!</p>
        </div>
      )}
    </div>
  );
};

export default SignupWithOtp;
